import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useJumboTheme } from "@jumbo/hooks";
import Div from "@jumbo/shared/Div";
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useReactToPrint } from "react-to-print";
import {
  createClient,
  getClients,
  updateClient,
} from "app/services/api/management";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Form, Formik, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { generateQrCode } from "./QrCodeGenerator";
import jsPDF from "jspdf";
import { toPng } from "html-to-image";
import PrintIcon from "@mui/icons-material/Print";

const ClientFormContent = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const [client, setClient] = useState(null);
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [qrCodeData, setQrCodeData] = useState();
  const handleGeneratePinCode = () => {
    setPinCode();
  };

  const setPinCode = () => {
    setFieldValue("pinCode", `${Math.floor(1000 + Math.random() * 9000)}`);
  };
  const handlePrint = useReactToPrint({
    content: () => {
      return qrRef.current;
    },
  });
  const qrRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loadingCompany, setLoadingCompany] = useState(false);

  // Načítanie firemných údajov z registra podľa IČO (priamo z frontendu).
  const handleLoadCompany = async () => {
    const ico = String(values.companyId || "").trim();
    if (!ico) {
      enqueueSnackbar(
        t("pages.dsokCustomers.validation.icoRequired", {
          defaultValue: "Zadajte IČO.",
        }),
        { variant: "warning" }
      );
      return;
    }
    setLoadingCompany(true);
    try {
      const res = await fetch(
        `https://api.orsf.sk/v1/companies/${encodeURIComponent(ico)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFieldValue("companyName", data?.name ?? "");
      setFieldValue("street", data?.street ?? data?.address?.street ?? "");
      setFieldValue("city", data?.city ?? data?.address?.city ?? "");
      setFieldValue("zipCode", data?.psc ?? data?.address?.postalCode ?? "");
      setFieldValue(
        "country",
        data?.countryCode ?? data?.address?.country ?? ""
      );
      setFieldValue("companyId", data?.ico ?? ico);
      setFieldValue("vatId", data?.icdph ?? data?.vatId ?? "");
      setFieldValue("taxId", data?.dic ?? data?.taxId ?? "");
      enqueueSnackbar(
        t("pages.dsokCustomers.notifications.companyLoaded", {
          defaultValue: "Firemné údaje boli načítané.",
        }),
        { variant: "success" }
      );
    } catch (err) {
      enqueueSnackbar(
        t("pages.dsokCustomers.notifications.companyLoadError", {
          defaultValue: "Nepodarilo sa načítať firmu podľa IČO.",
        }),
        { variant: "error" }
      );
    } finally {
      setLoadingCompany(false);
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      if (clientId && clientId !== "new") {
        const response = await getClients(clientId);
        setQrCodeData(response.qrCode);
        setClient(response);
        if (response?.companyName) {
          setFieldValue("companyName", response.companyName);
          setFieldValue("street", response.street);
          setFieldValue("city", response.city);
          setFieldValue("zipCode", response.zipCode);
          setFieldValue("country", response.country);
          setFieldValue("companyId", response.companyId);
          setFieldValue("vatId", response.vatId);
          setFieldValue("taxId", response.taxId);
          setFieldValue("email", response.email);
          setFieldValue("phone", response.phone);
          setFieldValue("firstName", response.firstName);
          setFieldValue("lastName", response.lastName);
          setFieldValue("pinCode", response.pinCode);
          setFieldValue("active", response.active);
          setFieldValue("payByInvoice", response.payByInvoice);
          setFieldValue("hideProductPrice", response.hideProductPrice ?? false);
        }
      } else {
        setPinCode();
        setFieldValue("companyName", "");
        setFieldValue("active", true);
      }
    };
    fetchClient();
  }, [clientId, setFieldValue]);

  return (
    <Form>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={values.active}
                  onChange={(e) => setFieldValue("active", e.target.checked)}
                  name="active"
                  color="primary"
                />
              }
              label={t("pages.dsokCustomers.active")}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="companyId"
              label={t("pages.dsokCustomers.companyId")}
              value={values.companyId}
              onChange={(e) => setFieldValue("companyId", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <Button
              variant="outlined"
              onClick={handleLoadCompany}
              disabled={loadingCompany}
              sx={{ height: "56px" }}
            >
              {loadingCompany
                ? t("pages.dsokCustomers.loading", {
                    defaultValue: "Načítavam…",
                  })
                : t("pages.dsokCustomers.loadCompany", {
                    defaultValue: "Načítať",
                  })}
            </Button>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="companyName"
              label={t("pages.dsokCustomers.companyName")}
              value={values.companyName}
              onChange={(e) => setFieldValue("companyName", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <Tooltip arrow title={t("pages.dsokCustomers.payByInvoiceHelp")}>
              <Box component="span">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.payByInvoice}
                      onChange={(e) =>
                        setFieldValue("payByInvoice", e.target.checked)
                      }
                      name="payByInvoice"
                      color="primary"
                    />
                  }
                  label={t("pages.dsokCustomers.payByInvoice")}
                />
              </Box>
            </Tooltip>
          </FormControl>
        </Grid>
        {/* Prepínač skrytia cien sa zobrazuje len ak má klient povolenú platbu faktúrou. */}
        {values.payByInvoice && (
          <Grid item xs={12} md={4} lg={3}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <Tooltip
                arrow
                title={t("pages.dsokCustomers.hideProductPriceHelp")}
              >
                <Box component="span">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.hideProductPrice ?? false}
                        onChange={(e) =>
                          setFieldValue("hideProductPrice", e.target.checked)
                        }
                        name="hideProductPrice"
                        color="primary"
                      />
                    }
                    label={t("pages.dsokCustomers.hideProductPrice")}
                  />
                </Box>
              </Tooltip>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={4} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="street"
              label={t("pages.dsokCustomers.street")}
              value={values.street}
              onChange={(e) => setFieldValue("street", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="zipCode"
              label={t("pages.dsokCustomers.zipCode")}
              value={values.zipCode}
              onChange={(e) => setFieldValue("zipCode", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="city"
              label={t("pages.dsokCustomers.city")}
              value={values.city}
              onChange={(e) => setFieldValue("city", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="country"
              label={t("pages.dsokCustomers.country")}
              value={values.country}
              onChange={(e) => setFieldValue("country", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="vatId"
              label={t("pages.dsokCustomers.vatId")}
              value={values.vatId}
              onChange={(e) => setFieldValue("vatId", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="taxId"
              label={t("pages.dsokCustomers.taxId")}
              value={values.taxId}
              onChange={(e) => setFieldValue("taxId", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="email"
              label={t("pages.dsokCustomers.email")}
              value={values.email}
              onChange={(e) => setFieldValue("email", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="phone"
              label={t("pages.dsokCustomers.phone")}
              value={values.phone}
              onChange={(e) => setFieldValue("phone", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="firstName"
              label={t("pages.dsokCustomers.firstName")}
              value={values.firstName}
              onChange={(e) => setFieldValue("firstName", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              name="lastName"
              label={t("pages.dsokCustomers.lastName")}
              value={values.lastName}
              onChange={(e) => setFieldValue("lastName", e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              fullWidth
              required
              name="pinCode"
              type="number"
              label={t("pages.dsokCustomers.pinCode")}
              value={values.pinCode}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 4) {
                  setFieldValue("pinCode", value);
                }
              }}
              inputProps={{
                maxLength: 4,
                pattern: "[0-9]*",
                inputMode: "numeric",
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <Button
              size="normal"
              variant="outlined"
              onClick={() => handleGeneratePinCode()}
            >
              {t("pages.dsokCustomers.generatePinCode")}
            </Button>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={12}></Grid>
        <Grid item xs={12} md={4} lg={2}>
          {clientId !== "new" && (
            <>
              <div ref={qrRef} className="qrCodePrint">
                <div className="print-only">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {generateQrCode({
                        data: qrCodeData,
                        size: 180,
                      })}
                    </Grid>
                    <Grid item xs={6}>
                      <div>{`${client?.companyName}`}</div>
                      <div>{`${client?.companyId || ""}`}</div>
                      <div>{`${client?.vatId || ""}`}</div>
                      <div>{`${client?.taxId || ""}`}</div>
                      <div>{`${client?.email}`}</div>
                      <div>{`${client?.phone}`}</div>
                      <div>{`${client?.street || ""}`}</div>
                      <div>{`${client?.zip || ""} ${client?.city || ""}`}</div>
                    </Grid>
                  </Grid>
                </div>
              </div>
              {generateQrCode({
                data: qrCodeData,
                size: 180,
              })}
            </>
          )}
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          {clientId !== "new" && (
            <>
              <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                <IconButton
                  onClick={async () => {
                    await new Promise((resolve) => {
                      setTimeout(() => {
                        resolve();
                      }, 500);
                    });
                    handlePrint();
                  }}
                >
                  <PrintIcon fontSize="large" />
                </IconButton>
              </Div>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Div
            sx={{
              mt: 2,
              mb: 1,
              mx: 2,
              gap: "4px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="normal"
              onClick={() => navigate("/customer/dsok/clients")}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="normal"
              disabled={isSubmitting}
            >
              {t("buttons.save")}
            </Button>
          </Div>
        </Grid>
      </Grid>
    </Form>
  );
};

const ClientForm = () => {
  const { t } = useTranslation();
  const { theme } = useJumboTheme();
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchClient = async () => {
      const response = await getClients(clientId);
      setClient(response);
    };
    fetchClient();
  }, [clientId]);
  const navigate = useNavigate();

  const companyName = client?.companyName || "";
  const handleSubmit = async (values, { setSubmitting }) => {
    const pinConflictMessage = t(
      "pages.dsokCustomers.notifications.pinCodeInUse",
      { defaultValue: "Tento PIN už používa iný klient. Zvoľte iný PIN." }
    );
    const isPinConflict = (payload) =>
      payload?.message === "pin_code_already_in_use" || payload?.code === 409;

    try {
      let ok = false;

      if (clientId && clientId === "new") {
        const response = await createClient(values);
        if (response?.code === 201) {
          ok = true;
          enqueueSnackbar(
            t("pages.dsokCustomers.notifications.customerCreated"),
            { variant: "success" }
          );
        } else if (isPinConflict(response)) {
          enqueueSnackbar(pinConflictMessage, { variant: "error" });
        } else {
          enqueueSnackbar(t("pages.dsokCustomers.notifications.error"), {
            variant: "error",
          });
        }
      } else {
        await updateClient(clientId, values);
        ok = true;
        enqueueSnackbar(t("pages.dsokCustomers.notifications.success"), {
          variant: "success",
        });
      }

      setSubmitting(false);
      // Presmeruj len pri úspechu — pri chybe (napr. duplicitný PIN) zostaň vo formulári.
      if (ok) {
        setTimeout(() => {
          navigate("/customer/dsok/clients");
        }, 2000);
      }
    } catch (error) {
      const payload = error?.response?.data;
      enqueueSnackbar(
        isPinConflict(payload)
          ? pinConflictMessage
          : t("pages.dsokCustomers.notifications.error"),
        { variant: "error" }
      );
      setSubmitting(false);
    }
  };
  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/clients">
          {t("pages.dsokDetails.clients.title")}
        </Link>
        <Typography color="text.primary">{companyName}</Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
        }}
      >
        <JumboCardQuick
          title={
            <Typography
              component={"div"}
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: 29,
                [theme.breakpoints.down("md")]: {
                  flexWrap: "wrap",
                },
              }}
            >
              <Typography
                variant={"h4"}
                mb={0}
                sx={{
                  minWidth: 245,
                  [theme.breakpoints.down("md")]: {
                    minWidth: "100%",
                    marginBottom: 2,
                  },
                }}
              >
                {companyName}
              </Typography>
              {clientId !== "new" && (
                <Box display="flex" flexDirection="row" gap={1} ml="auto">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigate(`/customer/dsok/clients/${clientId}/employees`)
                    }
                  >
                    {t("pages.dsokCustomers.employees.title")}
                  </Button>
                </Box>
              )}
            </Typography>
          }
          headerSx={{
            borderBottom: 1,
            borderBottomColor: "divider",
            "& .MuiCardHeader-action": {
              my: -0.75,
            },
          }}
          sx={{
            flex: 1,
            mr: 2,
          }}
          wrapperSx={{
            p: 1,
            "&:last-child": {
              pb: 2,
            },
            "& .MuiCollapse-entered:last-child": {
              "& .MuiListItemButton-root": {
                borderBottom: 0,
                borderBottomColor: "transparent",
              },
            },
          }}
        >
          <Formik
            initialValues={{
              companyName: companyName,
              street: client?.street,
              city: client?.city,
              zipCode: client?.zipCode,
              country: client?.country,
              companyId: client?.companyId,
              vatId: client?.vatId,
              taxId: client?.taxId,
              email: client?.email,
              phone: client?.phone,
              firstName: client?.firstName,
              lastName: client?.lastName,
              pinCode: client?.pinCode,
              active: client?.active,
              payByInvoice: client?.payByInvoice,
              hideProductPrice: client?.hideProductPrice ?? false,
            }}
            validationSchema={Yup.object().shape({
              companyId: Yup.string().required(
                t("pages.dsokCustomers.validation.icoRequired", {
                  defaultValue: "IČO je povinné.",
                })
              ),
              companyName: Yup.string().required(
                t("pages.dsokCustomers.validation.companyNameRequired")
              ),
              pinCode: Yup.string()
                .required(t("pages.dsokCustomers.validation.required"))
                .matches(
                  /^\d{4}$/,
                  t("pages.dsokCustomers.validation.pinCodeMustBe4Digits")
                )
                .length(
                  4,
                  t("pages.dsokCustomers.validation.pinCodeMustBe4Digits")
                ),
              email: Yup.string()
                .email(t("pages.dsokCustomers.validation.invalidEmail"))
                .required(t("pages.dsokCustomers.validation.required")),
              phone: Yup.string().required(
                t("pages.dsokCustomers.validation.required")
              ),
            })}
            onSubmit={handleSubmit}
          >
            <ClientFormContent />
          </Formik>
        </JumboCardQuick>
      </Div>
    </>
  );
};

export default ClientForm;
